import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { leadInputSchema, type Lead, type LeadInput } from '@fenix/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { leadsApi, productsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { sortByName } from '@/lib/format';

export function LeadFormDialog({
  open,
  onOpenChange,
  lead,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  lead?: Lead;
}) {
  const qc = useQueryClient();
  const products = useQuery({
    queryKey: ['products'],
    queryFn: () => productsApi.list(),
    enabled: open,
  });
  const form = useForm<LeadInput>({
    resolver: zodResolver(leadInputSchema),
    values: lead
      ? {
          name: lead.name,
          phone: lead.phoneRaw,
          email: lead.email,
          country: lead.country,
          state: lead.state,
          city: lead.city,
          productCodename: lead.productCodename,
          sourceSlug: lead.sourceSlug,
        }
      : { name: '', phone: '', email: null, country: 'Brazil', productCodename: null },
  });

  const mut = useMutation({
    mutationFn: (v: LeadInput) => (lead ? leadsApi.update(lead.id, v) : leadsApi.create(v)),
    onSuccess: () => {
      toast.success(lead ? 'Lead atualizado' : 'Lead criado');
      void qc.invalidateQueries({ queryKey: ['leads'] });
      onOpenChange(false);
      form.reset();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao salvar'),
  });

  const err = form.formState.errors;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{lead ? 'Editar lead' : 'Novo lead'}</DialogTitle>
          <DialogDescription>
            O telefone é normalizado automaticamente para o formato internacional com base no país.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => mut.mutate(v))}
          className="grid gap-4 sm:grid-cols-2"
          noValidate
        >
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Nome</Label>
            <Input {...form.register('name')} placeholder="Maria Silva" />
          </div>
          <div className="min-w-0 space-y-1.5">
            <Label>Telefone *</Label>
            <Input {...form.register('phone')} placeholder="(11) 99999-8888" />
            {err.phone && <p className="text-xs text-destructive">Telefone obrigatório</p>}
          </div>
          <div className="min-w-0 space-y-1.5">
            <Label>País</Label>
            <Input {...form.register('country')} placeholder="Brazil / United States" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>E-mail</Label>
            <Input type="email" {...form.register('email')} />
            {err.email && <p className="text-xs text-destructive">E-mail inválido</p>}
          </div>
          <div className="min-w-0 space-y-1.5">
            <Label>Produto</Label>
            <Select
              value={form.watch('productCodename') ?? ''}
              onValueChange={(v) => form.setValue('productCodename', v)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {sortByName(products.data ?? []).map((p) => (
                  <SelectItem key={p.codename} value={p.codename} title={p.codename}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0 space-y-1.5">
            <Label>Origem (source_slug)</Label>
            <Input {...form.register('sourceSlug')} placeholder="horsewood" />
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending && <Loader2 className="animate-spin" />} Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
